import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tabs from "@radix-ui/react-tabs";
import * as Toast from "@radix-ui/react-toast";
import { AlertCircle, CheckCircle2, ChevronDown, LogOut, RefreshCw, Wallet, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FormEvent } from "react";

import styles from "./App.module.css";
import {
  ApiRequestError,
  buildSendAction,
  estimateRelayFeeBudget,
  fetchBalance,
  fetchConfig,
  fetchGoogleKey,
  fetchWalletAssets,
  sendNotification,
  submitClaim,
} from "./api";
import { normalizeAddress } from "./bytes";
import {
  cardanoWalletSnapshot,
  clearCardanoWallet,
  connectCardanoWallet,
  submitCardanoSendAction,
  subscribeCardanoWallets,
} from "./cardano";
import type { CardanoWallet } from "./cardano";
import {
  approveErc20,
  connectWallet,
  currentEthereumProvider,
  formatUnits,
  parseUnits,
  readErc20Allowance,
  readErc20Balance,
  readErc20Decimals,
  readNativeBalance,
  sendWalletAction,
  selectWalletAccount,
  setEthereumProvider,
  shortAddress,
  switchNetwork,
  topUpRelayFee,
  walletChainId,
  waitForTransactionSuccess,
} from "./ethereum";
import { requestGoogleIdToken } from "./google";
import { buildClaimRequest, canonicalizeSendEmail, parseGoogleJwt } from "./sdk";
import type {
  AssetAmount,
  BalanceResponse,
  ChainConfig,
  ClaimRequest,
  ParsedJwt,
  PendingClaimError,
  PublicConfig,
  WalletAsset,
} from "./types";
import { connectWalletConnect, walletConnectConfigured } from "./walletConnect";
import type { InjectedWallet } from "./wallets";
import { subscribeInjectedWallets } from "./wallets";

type BusyState = "boot" | "connect" | "send" | "balance" | "claim" | "topup" | null;
type ToastKind = "success" | "error" | "info";
type WalletDialogMode = "connect" | "switch";
type WalletKind = "evm" | "cardano";
type TabValue = "send" | "claim";

interface TokenOption {
  id: string;
  address: string | null;
  chainAddress: string;
  name: string;
  symbol: string;
  decimals: number | null;
  logoURI?: string;
  native: boolean;
}

interface AppToast {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  txHash?: string;
}

interface ToastOptions {
  body?: string;
  txHash?: string;
}

interface BalanceSnapshot {
  account: string;
  chainKey: string;
  tokens: TokenOption[];
  values: Record<string, string>;
}

interface BalanceTotal {
  asset: string;
  amount: string;
  display?: AssetAmount["display"];
}

type EvmChainConfig = ChainConfig & { chain: { type: "evm"; chainId: number } };
type CardanoChainConfig = ChainConfig & { chain: { type: "cardano"; network: string } };
type DisplayChainConfig = EvmChainConfig | CardanoChainConfig;

interface RelayTopUpState {
  cellId: string;
  amountWei: bigint;
  requiredWei: bigint;
  availableWei: bigint;
}

interface ParsedRelayTopUp {
  suggestedTopUpCellId: string;
  shortfallWei: bigint;
  requiredWei: bigint;
  availableWei: bigint;
}

interface WalletSnapshot {
  account: string | null;
  chainId: number | null;
  cardanoNetworkId: number | null;
}

const NATIVE_TOKEN_ID = "native";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const CARDANO_MIN_CLAIM_PAYOUT_LOVELACE = 1_000_000n;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() ?? "";

let toastCounter = 0;

export function App() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [walletKind, setWalletKind] = useState<WalletKind | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [currentChainId, setCurrentChainId] = useState<number | null>(null);
  const [cardanoNetworkId, setCardanoNetworkId] = useState<number | null>(null);
  const [selectedTokenId, setSelectedTokenId] = useState(NATIVE_TOKEN_ID);
  const [sendEmail, setSendEmail] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [parsedJwt, setParsedJwt] = useState<ParsedJwt | null>(null);
  const [claimCredentialAvailable, setClaimCredentialAvailable] = useState(false);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [claimTokenDecimals, setClaimTokenDecimals] = useState<Record<string, number | null>>({});
  const [relayFeeEstimate, setRelayFeeEstimate] = useState<string | null>(null);
  const [relayTopUp, setRelayTopUp] = useState<RelayTopUpState | null>(null);
  const [tokenBalances, setTokenBalances] = useState<BalanceSnapshot | null>(null);
  const [busy, setBusy] = useState<BusyState>("boot");
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [toasts, setToasts] = useState<AppToast[]>([]);
  const [activeTab, setActiveTab] = useState<TabValue>("send");
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [walletDialogMode, setWalletDialogMode] = useState<WalletDialogMode>("connect");
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [injectedWallets, setInjectedWallets] = useState<InjectedWallet[]>([]);
  const [cardanoWallets, setCardanoWallets] = useState<CardanoWallet[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<EthereumProvider | null>(null);
  const compactJwtRef = useRef<string | null>(null);
  const busyRef = useRef<BusyState>("boot");

  const activateProvider = useCallback((provider: EthereumProvider | null) => {
    setEthereumProvider(provider);
    setSelectedProvider(provider);
  }, []);

  const showToast = useCallback((kind: ToastKind, title: string, options: ToastOptions = {}) => {
    const toast: AppToast = {
      id: `${Date.now()}-${toastCounter++}`,
      kind,
      title,
    };
    if (options.body !== undefined) {
      toast.body = options.body;
    }
    if (options.txHash !== undefined) {
      toast.txHash = options.txHash;
    }
    setToasts((current) => [...current.slice(-3), toast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const syncEvmWalletState = useCallback(async (): Promise<WalletSnapshot> => {
    const provider = currentEthereumProvider();
    if (provider === null) {
      setAccount(null);
      setCurrentChainId(null);
      return { account: null, chainId: null, cardanoNetworkId: null };
    }

    const accounts = await provider.request<string[]>({ method: "eth_accounts" });
    const firstAccount = accounts[0];
    const walletAccount = firstAccount === undefined ? null : normalizeAddress(firstAccount);
    const chainId = await walletChainId();
    setWalletKind(walletAccount === null ? null : "evm");
    setAccount(walletAccount);
    setCurrentChainId(chainId);
    setCardanoNetworkId(null);

    return { account: walletAccount, chainId, cardanoNetworkId: null };
  }, []);

  const syncCardanoWalletState = useCallback(async (): Promise<WalletSnapshot> => {
    try {
      const snapshot = await cardanoWalletSnapshot();
      setWalletKind("cardano");
      setAccount(snapshot.address);
      setCurrentChainId(null);
      setCardanoNetworkId(snapshot.networkId);
      return {
        account: snapshot.address,
        chainId: null,
        cardanoNetworkId: snapshot.networkId,
      };
    } catch {
      setWalletKind(null);
      setAccount(null);
      setCurrentChainId(null);
      setCardanoNetworkId(null);
      return { account: null, chainId: null, cardanoNetworkId: null };
    }
  }, []);

  useEffect(() => subscribeInjectedWallets(setInjectedWallets), []);
  useEffect(() => subscribeCardanoWallets(setCardanoWallets), []);

  useEffect(() => {
    let cancelled = false;
    busyRef.current = "boot";
    setBusy("boot");

    void (async () => {
      try {
        const loadedConfig = await fetchConfig();
        if (cancelled) {
          return;
        }
        setConfig(loadedConfig);
        await syncEvmWalletState();
      } catch (error) {
        showToast("error", "Backend unavailable", { body: errorMessage(error) });
      } finally {
        if (!cancelled) {
          busyRef.current = null;
          setBusy(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showToast, syncEvmWalletState]);

  useEffect(() => {
    if (selectedProvider === null) {
      return;
    }

    const handleWalletChange = () => {
      void syncEvmWalletState();
    };
    const handleDisconnect = () => {
      activateProvider(null);
      setWalletKind(null);
      setAccount(null);
      setCurrentChainId(null);
      setCardanoNetworkId(null);
    };

    selectedProvider.on?.("accountsChanged", handleWalletChange);
    selectedProvider.on?.("chainChanged", handleWalletChange);
    selectedProvider.on?.("disconnect", handleDisconnect);

    return () => {
      selectedProvider.removeListener?.("accountsChanged", handleWalletChange);
      selectedProvider.removeListener?.("chainChanged", handleWalletChange);
      selectedProvider.removeListener?.("disconnect", handleDisconnect);
    };
  }, [activateProvider, selectedProvider, syncEvmWalletState]);

  const activeChain = useMemo(() => {
    if (config === null) {
      return null;
    }
    if (walletKind === "cardano") {
      return cardanoNetworkId === null
        ? firstCardanoChain(config)
        : cardanoChainForNetworkId(config, cardanoNetworkId);
    }
    if (walletKind === "evm") {
      return currentChainId === null
        ? firstEvmChain(config)
        : evmChainForChainId(config, currentChainId);
    }
    return firstChain(config);
  }, [cardanoNetworkId, config, currentChainId, walletKind]);
  const displayChain = activeChain ?? firstChain(config);
  const tokenOptions = useMemo(() => defaultTokenOptions(displayChain), [displayChain]);
  const walletConnected = account !== null;
  const wrongNetwork = walletConnected && activeChain === null;

  useEffect(() => {
    if (displayChain === null) {
      setRelayFeeEstimate(null);
      return;
    }

    let cancelled = false;
    void estimateRelayFeeBudget(displayChain.chain, displayChain.nativeAsset, "1")
      .then((estimate) => {
        if (!cancelled) {
          setRelayFeeEstimate(estimate.amount);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRelayFeeEstimate(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [displayChain]);

  const effectiveTokenBalances =
    tokenBalances !== null &&
    account !== null &&
    displayChain !== null &&
    tokenBalances.account === walletAccountKey(displayChain, account) &&
    tokenBalances.chainKey === chainConfigKey(displayChain)
      ? tokenBalances.values
      : null;
  const spendableTokenOptions = useMemo(
    () => (effectiveTokenBalances === null || tokenBalances === null ? [] : tokenBalances.tokens),
    [effectiveTokenBalances, tokenBalances],
  );
  const activeTokenOptions = walletConnected ? spendableTokenOptions : tokenOptions;
  const selectedToken =
    activeTokenOptions.find((token) => token.id === selectedTokenId) ??
    activeTokenOptions[0] ??
    tokenOptions[0] ??
    null;
  const balancesPending =
    walletConnected && displayChain !== null && !wrongNetwork && effectiveTokenBalances === null;
  const balancesUnavailable = balancesLoading || balancesPending;

  useEffect(() => {
    if (
      !walletConnected &&
      tokenOptions.length > 0 &&
      tokenOptions.every((token) => token.id !== selectedTokenId)
    ) {
      setSelectedTokenId(tokenOptions[0]?.id ?? NATIVE_TOKEN_ID);
    }
  }, [selectedTokenId, tokenOptions, walletConnected]);

  useEffect(() => {
    if (account === null || displayChain === null || wrongNetwork) {
      setTokenBalances(null);
      setBalancesLoading(false);
      return;
    }

    let cancelled = false;
    setBalancesLoading(true);
    void fetchWalletAssets(displayChain.chain, account)
      .then(async (response) => {
        const enrichedAssets = await Promise.all(
          response.map(async (asset): Promise<WalletAsset> => {
            if (
              displayChain.chain.type === "cardano" ||
              isNativeAsset(displayChain, asset.asset) ||
              asset.display?.decimals !== undefined
            ) {
              return asset;
            }
            const decimals = await readErc20Decimals(asset.asset).catch(() => undefined);
            return decimals === undefined
              ? asset
              : {
                  ...asset,
                  display: {
                    ...asset.display,
                    decimals,
                  },
                };
          }),
        );
        if (!cancelled) {
          const spendableAssets = enrichedAssets.filter(
            (asset) =>
              isNativeAsset(displayChain, asset.asset) ||
              (displayChain.chain.type === "evm" && asset.display?.decimals !== undefined),
          );
          const tokens = spendableAssets.map((asset) => walletAssetToOption(displayChain, asset));
          setTokenBalances({
            account: walletAccountKey(displayChain, account),
            chainKey: chainConfigKey(displayChain),
            tokens,
            values: walletAssetBalances(tokens, spendableAssets),
          });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setTokenBalances({
            account: walletAccountKey(displayChain, account),
            chainKey: chainConfigKey(displayChain),
            tokens: [],
            values: {},
          });
          showToast("error", "Could not load wallet balances", { body: errorMessage(error) });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBalancesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [account, displayChain, showToast, wrongNetwork]);

  useEffect(() => {
    if (account === null || effectiveTokenBalances === null) {
      return;
    }
    if (
      spendableTokenOptions.length > 0 &&
      spendableTokenOptions.every((token) => token.id !== selectedTokenId)
    ) {
      setSelectedTokenId(spendableTokenOptions[0]?.id ?? NATIVE_TOKEN_ID);
    }
  }, [account, effectiveTokenBalances, selectedTokenId, spendableTokenOptions]);

  const claimTotals = useMemo(
    () => balanceTotals(displayChain, balance ?? []),
    [balance, displayChain],
  );

  useEffect(() => {
    if (displayChain?.chain.type !== "evm") {
      return;
    }
    const missingTotals =
      claimTotals.filter((total) => {
        const tokenKey = total.asset.toLowerCase();
        return (
          !isNativeAsset(displayChain, total.asset) &&
          tokenKey !== ZERO_ADDRESS &&
          total.display?.decimals === undefined &&
          claimTokenDecimals[tokenKey] === undefined
        );
      }) ?? [];
    if (missingTotals.length === 0) {
      return;
    }

    let cancelled = false;
    void Promise.all(
      missingTotals.map(async (total): Promise<[string, number | null]> => {
        const tokenKey = total.asset.toLowerCase();
        const decimals = await readErc20Decimals(total.asset).catch(() => null);
        return [tokenKey, decimals];
      }),
    ).then((entries) => {
      if (cancelled) {
        return;
      }
      setClaimTokenDecimals((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [claimTokenDecimals, claimTotals, displayChain]);

  const runAction = useCallback(
    async (nextBusy: NonNullable<BusyState>, action: () => Promise<void>) => {
      if (busyRef.current !== null) {
        return;
      }

      busyRef.current = nextBusy;
      setBusy(nextBusy);
      try {
        await action();
      } catch (error) {
        showToast("error", "Action failed", { body: errorMessage(error) });
      } finally {
        busyRef.current = null;
        setBusy(null);
      }
    },
    [showToast],
  );

  const openWalletDialog = useCallback((mode: WalletDialogMode) => {
    setWalletDialogMode(mode);
    setWalletDialogOpen(true);
  }, []);

  const connectInjectedWalletAction = useCallback(
    async (wallet: InjectedWallet, mode: WalletDialogMode) => {
      const previousProvider = currentEthereumProvider();
      clearCardanoWallet();
      activateProvider(wallet.provider);
      try {
        const selectedAccount =
          mode === "switch" ? await selectWalletAccount() : await connectWallet();
        const walletState = await syncEvmWalletState();
        const activeAccount = walletState.account ?? selectedAccount;
        setWalletKind("evm");
        setAccount(activeAccount);
        setCardanoNetworkId(null);
        setWalletDialogOpen(false);
        showToast("success", mode === "switch" ? "Wallet switched" : "Wallet connected", {
          body: `${wallet.name} ${shortAddress(activeAccount)}`,
        });
      } catch (error) {
        activateProvider(previousProvider);
        throw error;
      }
    },
    [activateProvider, showToast, syncEvmWalletState],
  );

  const connectCardanoWalletAction = useCallback(
    async (wallet: CardanoWallet, mode: WalletDialogMode) => {
      activateProvider(null);
      const connection = await connectCardanoWallet(wallet);
      setWalletKind("cardano");
      setAccount(connection.address);
      setCurrentChainId(null);
      setCardanoNetworkId(connection.networkId);
      setWalletDialogOpen(false);
      showToast("success", mode === "switch" ? "Wallet switched" : "Wallet connected", {
        body: `${wallet.name} ${shortHash(connection.address)}`,
      });
    },
    [activateProvider, showToast],
  );

  const connectWalletConnectAction = useCallback(
    async (mode: WalletDialogMode) => {
      if (displayChain === null || displayChain.chain.type !== "evm") {
        throw new Error("No enabled EVM chain is configured");
      }
      clearCardanoWallet();
      const connection = await connectWalletConnect(displayChain);
      activateProvider(connection.provider);
      const walletState = await syncEvmWalletState();
      const activeAccount = walletState.account ?? normalizeAddress(connection.account);
      setWalletKind("evm");
      setAccount(activeAccount);
      setCardanoNetworkId(null);
      setWalletDialogOpen(false);
      showToast("success", mode === "switch" ? "Wallet switched" : "Wallet connected", {
        body: `WalletConnect ${shortAddress(activeAccount)}`,
      });
    },
    [activateProvider, displayChain, showToast, syncEvmWalletState],
  );

  const switchNetworkAction = useCallback(async () => {
    if (displayChain === null || displayChain.chain.type !== "evm") {
      throw new Error("No enabled EVM chain is configured");
    }
    await switchNetwork(displayChain);
    await syncEvmWalletState();
    showToast("success", "Network selected", { body: displayChain.name });
  }, [displayChain, showToast, syncEvmWalletState]);

  const disconnectWallet = useCallback(() => {
    const disconnect = currentEthereumProvider()?.disconnect?.();
    void disconnect?.catch(() => undefined);
    activateProvider(null);
    clearCardanoWallet();
    setWalletKind(null);
    setAccount(null);
    setCurrentChainId(null);
    setCardanoNetworkId(null);
    showToast("info", "Wallet disconnected");
  }, [activateProvider, showToast]);

  const clearGoogleConnection = useCallback(() => {
    compactJwtRef.current = null;
    setClaimCredentialAvailable(false);
    setParsedJwt(null);
    setBalance(null);
    setClaimTokenDecimals({});
    setRelayTopUp(null);
  }, []);

  const logoutGoogle = useCallback(() => {
    clearGoogleConnection();
    showToast("info", "Google disconnected");
  }, [clearGoogleConnection, showToast]);

  const ensureWallet = useCallback(async () => {
    if (displayChain === null) {
      throw new Error("No enabled chain is configured");
    }

    if (displayChain.chain.type === "cardano") {
      const walletState = await syncCardanoWalletState();
      if (walletState.account === null) {
        throw new Error("No Cardano wallet account selected");
      }
      const selectedChain =
        config === null || walletState.cardanoNetworkId === null
          ? null
          : cardanoChainForNetworkId(config, walletState.cardanoNetworkId);
      if (selectedChain === null || !sameChainConfig(selectedChain, displayChain)) {
        throw new Error("Switch Cardano network");
      }
      return walletState.account;
    }

    let walletState = await syncEvmWalletState();
    let selectedAccount = walletState.account;
    if (selectedAccount === null) {
      selectedAccount = await connectWallet();
      setAccount(selectedAccount);
      walletState = await syncEvmWalletState();
      selectedAccount = walletState.account ?? selectedAccount;
    }

    if (walletState.chainId !== displayChain.chain.chainId) {
      await switchNetwork(displayChain);
      walletState = await syncEvmWalletState();
      selectedAccount = walletState.account ?? selectedAccount;
    }
    if (selectedAccount === null) {
      throw new Error("No wallet account selected");
    }

    return selectedAccount;
  }, [config, displayChain, syncCardanoWalletState, syncEvmWalletState]);

  const assertWalletStillSelected = useCallback(
    async (expectedAccount: string) => {
      if (displayChain === null) {
        throw new Error("Switch network");
      }

      const walletState =
        displayChain.chain.type === "cardano"
          ? await syncCardanoWalletState()
          : await syncEvmWalletState();
      if (displayChain.chain.type === "cardano") {
        const selectedChain =
          config === null || walletState.cardanoNetworkId === null
            ? null
            : cardanoChainForNetworkId(config, walletState.cardanoNetworkId);
        if (selectedChain === null || !sameChainConfig(selectedChain, displayChain)) {
          throw new Error("Switch network");
        }
        if (walletState.account === null || walletState.account !== expectedAccount) {
          throw new Error("Wallet account changed");
        }
        return;
      }

      if (walletState.chainId !== displayChain.chain.chainId) {
        throw new Error("Switch network");
      }
      if (
        walletState.account === null ||
        normalizeAddress(walletState.account) !== expectedAccount
      ) {
        throw new Error("Wallet account changed");
      }
    },
    [config, displayChain, syncCardanoWalletState, syncEvmWalletState],
  );

  const sendAction = useCallback(async () => {
    if (displayChain === null) {
      throw new Error("No enabled chain is configured");
    }
    if (selectedToken === null) {
      throw new Error("Asset is required");
    }

    const from = await ensureWallet();
    const toEmail = await canonicalizeSendEmail(sendEmail);
    const amountDecimals = selectedToken.decimals;
    if (amountDecimals === null) {
      throw new Error("Asset metadata is unavailable");
    }
    const amount = parseUnits(sendAmount, amountDecimals);
    if (amount === 0n) {
      throw new Error("Amount must be greater than zero");
    }
    const relayEstimate = await estimateRelayFeeBudget(
      displayChain.chain,
      selectedToken.chainAddress,
      amount.toString(),
    );
    setRelayFeeEstimate(relayEstimate.amount);
    const relayFeeWei = BigInt(relayEstimate.amount);
    const notifyRecipient = (txHash: string) => {
      void sendNotification(displayChain.chain, txHash, toEmail).catch((error: unknown) => {
        showToast("info", "Email notification pending", { body: errorMessage(error) });
      });
    };

    if (displayChain.chain.type === "cardano") {
      if (effectiveTokenBalances === null) {
        throw new Error("Wallet balance is not loaded");
      }
      const available = BigInt(effectiveTokenBalances[selectedToken.id] ?? "0");
      const nativeAvailable = BigInt(effectiveTokenBalances[NATIVE_TOKEN_ID] ?? "0");
      const requiredNative = selectedToken.native
        ? amount + relayFeeWei
        : CARDANO_MIN_CLAIM_PAYOUT_LOVELACE + relayFeeWei;
      if (available < amount || nativeAvailable < requiredNative) {
        throw new Error("Insufficient balance");
      }
      await assertWalletStillSelected(from);
      const action = await buildSendAction({
        chain: displayChain.chain,
        from,
        toEmail,
        asset: selectedToken.chainAddress,
        amount: amount.toString(),
        timeout: displayChain.defaults.refundTimeoutSeconds,
      });
      const txHash = await submitCardanoSendAction(displayChain.chain.network, action);
      showToast("success", "Send submitted", { txHash });
      notifyRecipient(txHash);
      setSendAmount("");
      return;
    }

    if (selectedToken.native) {
      const nativeBalance = await readNativeBalance(from);
      if (nativeBalance < amount + relayFeeWei) {
        throw new Error("Insufficient balance");
      }
      await assertWalletStillSelected(from);
      const action = await buildSendAction({
        chain: displayChain.chain,
        from,
        toEmail,
        asset: selectedToken.chainAddress,
        amount: amount.toString(),
        timeout: displayChain.defaults.refundTimeoutSeconds,
      });
      const txHash = await sendWalletAction({
        from,
        action,
      });
      showToast("success", "Send submitted", { txHash });
      notifyRecipient(txHash);
      setSendAmount("");
      return;
    }

    if (selectedToken.address === null) {
      throw new Error("ERC-20 token address is required");
    }

    const tokenBalance = await readErc20Balance(selectedToken.address, from);
    if (tokenBalance < amount) {
      throw new Error("Insufficient balance");
    }
    const nativeBalance = await readNativeBalance(from);
    if (nativeBalance < relayFeeWei) {
      throw new Error("Insufficient balance");
    }

    const allowance = await readErc20Allowance({
      tokenAddress: selectedToken.address,
      owner: from,
      spender: displayChain.escrow,
    });
    if (allowance < amount) {
      if (allowance > 0n) {
        await assertWalletStillSelected(from);
        const resetTxHash = await approveErc20({
          from,
          tokenAddress: selectedToken.address,
          spender: displayChain.escrow,
          amount: 0n,
        });
        showToast("success", "Approval reset submitted", { txHash: resetTxHash });
        await waitForTransactionSuccess(resetTxHash, "Approval reset");
        showToast("success", "Approval reset confirmed", { txHash: resetTxHash });
        await assertWalletStillSelected(from);
        const refreshedNativeBalance = await readNativeBalance(from);
        if (refreshedNativeBalance < relayFeeWei) {
          throw new Error("Insufficient balance");
        }
      }
      await assertWalletStillSelected(from);
      const approveTxHash = await approveErc20({
        from,
        tokenAddress: selectedToken.address,
        spender: displayChain.escrow,
        amount,
      });
      showToast("success", "Approval submitted", { txHash: approveTxHash });
      await waitForTransactionSuccess(approveTxHash, "Approval");
      showToast("success", "Approval confirmed", { txHash: approveTxHash });
      await assertWalletStillSelected(from);
      const refreshedNativeBalance = await readNativeBalance(from);
      if (refreshedNativeBalance < relayFeeWei) {
        throw new Error("Insufficient balance");
      }
    }

    await assertWalletStillSelected(from);
    const action = await buildSendAction({
      chain: displayChain.chain,
      from,
      toEmail,
      asset: selectedToken.chainAddress,
      amount: amount.toString(),
      timeout: displayChain.defaults.refundTimeoutSeconds,
    });
    const txHash = await sendWalletAction({
      from,
      action,
    });
    showToast("success", "Send submitted", { txHash });
    notifyRecipient(txHash);
    setSendAmount("");
  }, [
    assertWalletStillSelected,
    displayChain,
    ensureWallet,
    effectiveTokenBalances,
    selectedToken,
    sendAmount,
    sendEmail,
    showToast,
  ]);

  const handleSendSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void runAction("send", sendAction);
    },
    [runAction, sendAction],
  );

  const useJwt = useCallback(
    async (jwt: string) => {
      if (displayChain === null) {
        throw new Error("No enabled chain is configured");
      }
      const parsed = await parseGoogleJwt(jwt);
      const updatedBalance = await fetchBalance(displayChain.chain, parsed.email);
      compactJwtRef.current = jwt;
      setClaimCredentialAvailable(true);
      setParsedJwt(parsed);
      setBalance(updatedBalance);
      setClaimTokenDecimals({});
      setRelayTopUp(null);
      setActiveTab("claim");
      if (updatedBalance.length > 0) {
        showToast("success", "Google connected", { body: parsed.email });
      }
    },
    [displayChain, showToast],
  );

  const handleGoogleCredential = useCallback(
    (jwt: string) =>
      runAction("balance", async () => {
        await useJwt(jwt);
      }),
    [runAction, useJwt],
  );

  const claimAction = useCallback(async () => {
    if (displayChain === null) {
      throw new Error("No enabled chain is configured");
    }
    if (parsedJwt === null) {
      throw new Error("Google login is required");
    }
    const compactJwt = compactJwtRef.current;
    if (compactJwt === null) {
      throw new Error("Google login is required");
    }

    const to = await ensureWallet();
    const currentBalance = balance ?? (await fetchBalance(displayChain.chain, parsedJwt.email));
    if (currentBalance.length === 0) {
      throw new Error("No funds to claim");
    }
    setRelayTopUp(null);

    const googleKey = await fetchGoogleKey(parsedJwt.kid);
    const proofChain =
      displayChain.chain.type === "evm"
        ? {
            ...displayChain.chain,
            escrow: displayChain.escrow,
          }
        : displayChain.chain;
    const claimChunks = chunkArray(
      currentBalance.map((cell) => cell.ref),
      displayChain.limits.claimRequestCap,
    );
    const claimRequests: ClaimRequest[] = [];
    try {
      for (const cellIds of claimChunks) {
        claimRequests.push(
          await buildClaimRequest({
            compactJwt,
            googleKey,
            chain: proofChain,
            refs: cellIds,
            to,
            relayer: displayChain.relayer,
          }),
        );
      }
    } finally {
      compactJwtRef.current = null;
      setClaimCredentialAvailable(false);
    }
    for (const request of claimRequests) {
      await assertWalletStillSelected(to);

      let response;
      try {
        response = await submitClaim(request);
      } catch (error) {
        const topUp = parseRelayFeeTopUp(error);
        if (topUp !== null) {
          setRelayTopUp({
            cellId: topUp.suggestedTopUpCellId,
            amountWei: topUp.shortfallWei,
            requiredWei: topUp.requiredWei,
            availableWei: topUp.availableWei,
          });
          showToast("info", "Relay fee top-up required", {
            body: `Add ${formatNative(displayChain, topUp.shortfallWei.toString())}`,
          });
          return;
        }

        const pendingClaim = parsePendingClaim(error);
        if (pendingClaim !== null) {
          setBalance(await fetchBalance(displayChain.chain, parsedJwt.email));
          showPendingClaimToasts(showToast, pendingClaim);
          return;
        }
        throw error;
      }

      for (const txHash of response) {
        showToast("success", "Claim submitted", { txHash });
      }
    }

    setBalance(await fetchBalance(displayChain.chain, parsedJwt.email));
    setRelayTopUp(null);
  }, [assertWalletStillSelected, balance, displayChain, ensureWallet, parsedJwt, showToast]);

  const topUpRelayFeeAction = useCallback(async () => {
    if (displayChain === null || displayChain.chain.type !== "evm") {
      throw new Error("No enabled EVM chain is configured");
    }
    if (parsedJwt === null) {
      throw new Error("Google login is required");
    }
    if (relayTopUp === null) {
      throw new Error("Relay fee top-up is not required");
    }

    const from = await ensureWallet();
    const txHash = await topUpRelayFee({
      from,
      contractAddress: displayChain.escrow,
      cellId: relayTopUp.cellId,
      amountWei: relayTopUp.amountWei,
    });
    showToast("success", "Top-up submitted", { txHash });
    await waitForTransactionSuccess(txHash, "Relay fee top-up");
    showToast("success", "Top-up confirmed", { txHash });
    setBalance(await fetchBalance(displayChain.chain, parsedJwt.email));
    setRelayTopUp(null);
  }, [displayChain, ensureWallet, parsedJwt, relayTopUp, showToast]);

  const sendDisabled =
    busy === "send" ||
    displayChain === null ||
    selectedToken === null ||
    wrongNetwork ||
    balancesUnavailable ||
    (walletConnected &&
      effectiveTokenBalances !== null &&
      spendableTokenOptions.every((token) => token.id !== selectedToken.id)) ||
    sendEmail.trim() === "" ||
    sendAmount.trim() === "" ||
    sendAmountExceedsSelectedBalance({
      config: displayChain,
      relayFeeEstimate,
      selectedToken,
      sendAmount,
      tokenBalances: effectiveTokenBalances,
    });
  const sendButtonLabel = sendActionLabel({
    busy,
    balancesLoading: balancesUnavailable,
    config: displayChain,
    selectedToken,
    spendableTokenOptions,
    tokenBalances: effectiveTokenBalances,
    walletConnected,
    wrongNetwork,
    sendEmail,
    sendAmount,
    amountExceedsBalance: sendAmountExceedsSelectedBalance({
      config: displayChain,
      relayFeeEstimate,
      selectedToken,
      sendAmount,
      tokenBalances: effectiveTokenBalances,
    }),
  });
  const assetSelectionDisabled =
    !walletConnected || wrongNetwork || balancesUnavailable || spendableTokenOptions.length === 0;
  const claimableCells = balance?.length ?? 0;
  const claimCredentialRequired =
    parsedJwt === null || (!claimCredentialAvailable && relayTopUp === null);
  const claimDisabled =
    busy === "claim" ||
    busy === "topup" ||
    displayChain === null ||
    wrongNetwork ||
    claimableCells === 0 ||
    (!claimCredentialAvailable && relayTopUp === null);

  return (
    <Toast.Provider swipeDirection="right">
      <main className={styles.app}>
        <div className={styles.shell}>
          <header className={styles.topBar}>
            <a className={styles.brand} href="/" aria-label="zkFold Smart Wallet">
              <img
                className={styles.brandLogo}
                src="/images/logo/logo-mark.svg"
                alt="zkFold"
                width="40"
                height="40"
              />
              <span className={styles.brandText}>
                <span className={styles.brandTitle}>Smart Wallet</span>
                {displayChain !== null ? (
                  <span className={styles.brandSubtitle}>{displayChain.name}</span>
                ) : null}
              </span>
            </a>

            <WalletControl
              account={account}
              busy={busy === "connect"}
              config={displayChain}
              currentChainId={currentChainId}
              wrongNetwork={wrongNetwork}
              onConnect={() => openWalletDialog("connect")}
              onDisconnect={disconnectWallet}
              onSwitchNetwork={() => {
                void runAction("connect", switchNetworkAction);
              }}
              onSwitchWallet={() => openWalletDialog("switch")}
            />
          </header>

          <section className={styles.workspace} aria-label="Send and claim funds">
            <Tabs.Root value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)}>
              <div className={styles.card}>
                <Tabs.List className={styles.tabsList} aria-label="Transfer mode">
                  <Tabs.Trigger className={styles.tabTrigger} value="send">
                    Send
                  </Tabs.Trigger>
                  <Tabs.Trigger className={styles.tabTrigger} value="claim">
                    Claim
                  </Tabs.Trigger>
                </Tabs.List>

                <Tabs.Content value="send">
                  <form className={styles.form} onSubmit={handleSendSubmit}>
                    <label className={styles.field}>
                      <span>Gmail recipient</span>
                      <input
                        className={styles.input}
                        type="email"
                        value={sendEmail}
                        placeholder="name@gmail.com"
                        autoComplete="email"
                        onChange={(event) => setSendEmail(event.currentTarget.value)}
                      />
                    </label>

                    <label className={styles.field}>
                      <span>Asset</span>
                      <div className={styles.amountBlock}>
                        <input
                          className={styles.amountInput}
                          type="text"
                          inputMode="decimal"
                          value={sendAmount}
                          placeholder="0"
                          aria-label="Amount"
                          onChange={(event) => setSendAmount(event.currentTarget.value)}
                        />
                        <button
                          className={styles.assetTrigger}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => setAssetDialogOpen(true)}
                          disabled={assetSelectionDisabled}
                        >
                          {selectedToken === null ? (
                            <span>Select</span>
                          ) : (
                            <TokenBadge config={displayChain} token={selectedToken} />
                          )}
                          <ChevronDown size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </label>

                    <p className={styles.secondaryLine}>
                      {sendSecondaryLine(displayChain, wrongNetwork, relayFeeEstimate)}
                    </p>

                    {walletConnected ? (
                      <button
                        className={styles.primaryButton}
                        type="submit"
                        disabled={sendDisabled}
                      >
                        {sendButtonLabel}
                      </button>
                    ) : (
                      <button
                        className={styles.primaryButton}
                        type="button"
                        onClick={() => openWalletDialog("connect")}
                      >
                        Connect wallet
                      </button>
                    )}
                  </form>
                </Tabs.Content>

                <Tabs.Content value="claim">
                  <div className={styles.claimPanel}>
                    {parsedJwt === null ? null : (
                      <GoogleAccountControl email={parsedJwt.email} onLogout={logoutGoogle} />
                    )}

                    {claimCredentialRequired ? (
                      <div className={styles.emptyClaimState}>
                        {walletConnected ? (
                          config === null ? (
                            <button
                              className={`${styles.primaryButton} ${styles.googleFallbackButton}`}
                              type="button"
                              disabled
                            >
                              Continue with Google
                            </button>
                          ) : (
                            <GoogleLoginButton
                              clientId={GOOGLE_CLIENT_ID}
                              onCredential={handleGoogleCredential}
                              onError={(error) => {
                                showToast("error", "Google login failed", {
                                  body: errorMessage(error),
                                });
                              }}
                            />
                          )
                        ) : (
                          <button
                            className={styles.primaryButton}
                            type="button"
                            onClick={() => openWalletDialog("connect")}
                          >
                            Connect wallet
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <AssetTotals
                          claimTokenDecimals={claimTokenDecimals}
                          config={displayChain}
                          totals={claimTotals}
                        />
                        {relayTopUp !== null ||
                        wrongNetwork ||
                        displayChain === null ||
                        claimableCells > 0 ? (
                          <p className={styles.secondaryLine}>
                            {relayTopUp === null
                              ? claimSecondaryLine(displayChain, wrongNetwork, claimableCells)
                              : `Needs ${formatNative(displayChain, relayTopUp.amountWei.toString())} more`}
                          </p>
                        ) : null}
                        <button
                          className={styles.primaryButton}
                          type="button"
                          disabled={claimDisabled}
                          onClick={() => {
                            if (relayTopUp === null) {
                              void runAction("claim", claimAction);
                            } else {
                              void runAction("topup", topUpRelayFeeAction);
                            }
                          }}
                        >
                          {claimButtonLabel(busy, relayTopUp)}
                        </button>
                      </>
                    )}
                  </div>
                </Tabs.Content>
              </div>
            </Tabs.Root>
          </section>
        </div>
      </main>

      <WalletDialog
        mode={walletDialogMode}
        open={walletDialogOpen}
        busy={busy === "connect"}
        cardanoWallets={cardanoWallets}
        injectedWallets={injectedWallets}
        walletConnectAvailable={
          displayChain !== null && displayChain.chain.type === "evm" && walletConnectConfigured()
        }
        onOpenChange={setWalletDialogOpen}
        onCardanoWallet={(wallet) => {
          void runAction("connect", async () => {
            await connectCardanoWalletAction(wallet, walletDialogMode);
          });
        }}
        onInjectedWallet={(wallet) => {
          void runAction("connect", async () => {
            await connectInjectedWalletAction(wallet, walletDialogMode);
          });
        }}
        onWalletConnect={() => {
          void runAction("connect", async () => {
            await connectWalletConnectAction(walletDialogMode);
          });
        }}
      />

      <AssetDialog
        config={displayChain}
        open={assetDialogOpen}
        selectedTokenId={selectedTokenId}
        tokens={spendableTokenOptions}
        onOpenChange={setAssetDialogOpen}
        onSelect={(tokenId) => {
          if (tokenId !== selectedTokenId) {
            setSendAmount("");
          }
          setSelectedTokenId(tokenId);
          setAssetDialogOpen(false);
        }}
      />

      <Toast.Viewport className={styles.toastViewport} />
      {toasts.map((toast) => (
        <Toast.Root
          key={toast.id}
          className={`${styles.toastRoot} ${styles[toast.kind]}`}
          duration={toast.txHash === undefined ? 6000 : 9000}
          onOpenChange={(open) => {
            if (!open) {
              removeToast(toast.id);
            }
          }}
        >
          <div className={styles.toastIcon} aria-hidden="true">
            {toast.kind === "error" ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
          </div>
          <div className={styles.toastBody}>
            <Toast.Title className={styles.toastTitle}>{toast.title}</Toast.Title>
            {toast.body === undefined ? null : (
              <Toast.Description className={styles.toastDescription}>
                {toast.body}
              </Toast.Description>
            )}
            {toast.txHash === undefined ? null : (
              <TransactionLink config={displayChain} txHash={toast.txHash} />
            )}
          </div>
          <Toast.Close className={styles.toastClose} aria-label="Close">
            <X size={14} aria-hidden="true" />
          </Toast.Close>
        </Toast.Root>
      ))}
    </Toast.Provider>
  );
}

function WalletControl(props: {
  account: string | null;
  busy: boolean;
  config: DisplayChainConfig | null;
  currentChainId: number | null;
  wrongNetwork: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onSwitchNetwork: () => void;
  onSwitchWallet: () => void;
}) {
  const switchableNetwork =
    props.wrongNetwork &&
    props.config !== null &&
    props.currentChainId !== null &&
    props.config.chain.type === "evm" &&
    props.currentChainId !== props.config.chain.chainId;

  if (props.account === null) {
    return (
      <button
        className={styles.walletButton}
        type="button"
        onClick={props.onConnect}
        disabled={props.busy}
      >
        <Wallet size={16} aria-hidden="true" />
        Connect wallet
      </button>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={styles.walletButton}>
        <Wallet size={16} aria-hidden="true" />
        {shortWalletAddress(props.account, props.config)}
        <ChevronDown size={14} aria-hidden="true" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={styles.dropdownContent} align="end" sideOffset={8}>
          <DropdownMenu.Item className={styles.dropdownItem} onSelect={props.onSwitchWallet}>
            <RefreshCw size={15} aria-hidden="true" />
            Switch wallet
          </DropdownMenu.Item>
          {switchableNetwork ? (
            <DropdownMenu.Item className={styles.dropdownItem} onSelect={props.onSwitchNetwork}>
              <Wallet size={15} aria-hidden="true" />
              Switch network
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Separator className={styles.dropdownSeparator} />
          <DropdownMenu.Item className={styles.dropdownItem} onSelect={props.onDisconnect}>
            <LogOut size={15} aria-hidden="true" />
            Disconnect
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function GoogleAccountControl(props: { email: string; onLogout: () => void }) {
  return (
    <div className={styles.connectedEmail}>
      <span className={styles.connectedEmailText}>
        <span>Gmail</span>
        <strong>{props.email}</strong>
      </span>
      <button
        className={styles.emailLogoutButton}
        type="button"
        onClick={props.onLogout}
        aria-label="Log out of Google"
        title="Log out"
      >
        <LogOut size={15} aria-hidden="true" />
      </button>
    </div>
  );
}

function WalletDialog(props: {
  mode: WalletDialogMode;
  open: boolean;
  busy: boolean;
  cardanoWallets: CardanoWallet[];
  injectedWallets: InjectedWallet[];
  walletConnectAvailable: boolean;
  onCardanoWallet: (wallet: CardanoWallet) => void;
  onInjectedWallet: (wallet: InjectedWallet) => void;
  onWalletConnect: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  const noOptions =
    props.injectedWallets.length === 0 &&
    props.cardanoWallets.length === 0 &&
    !props.walletConnectAvailable;

  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Dialog.Content className={styles.dialogContent}>
          <div className={styles.dialogHeader}>
            <Dialog.Title>
              {props.mode === "switch" ? "Switch wallet" : "Connect wallet"}
            </Dialog.Title>
            <Dialog.Close className={styles.iconButton} aria-label="Close">
              <X size={16} aria-hidden="true" />
            </Dialog.Close>
          </div>
          {props.injectedWallets.map((wallet) => (
            <button
              key={wallet.id}
              className={styles.walletOption}
              type="button"
              onClick={() => props.onInjectedWallet(wallet)}
              disabled={props.busy}
            >
              <WalletOptionIcon icon={wallet.icon} />
              <span>{wallet.name}</span>
            </button>
          ))}
          {props.cardanoWallets.map((wallet) => (
            <button
              key={`cardano:${wallet.id}`}
              className={styles.walletOption}
              type="button"
              onClick={() => props.onCardanoWallet(wallet)}
              disabled={props.busy}
            >
              <WalletOptionIcon icon={wallet.icon} />
              <span>{wallet.name}</span>
            </button>
          ))}
          <button
            className={styles.walletOption}
            type="button"
            onClick={props.onWalletConnect}
            disabled={props.busy || !props.walletConnectAvailable}
            title={
              props.walletConnectAvailable
                ? "WalletConnect"
                : "Set VITE_WALLETCONNECT_PROJECT_ID to enable WalletConnect"
            }
          >
            <WalletOptionIcon />
            <span>WalletConnect</span>
          </button>
          {noOptions ? (
            <button className={styles.walletOption} type="button" disabled>
              <WalletOptionIcon />
              <span>No wallet available</span>
            </button>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function WalletOptionIcon(props: { icon?: string | undefined }) {
  if (props.icon !== undefined && props.icon.length > 0) {
    return (
      <img className={styles.walletOptionIcon} src={props.icon} alt="" width="28" height="28" />
    );
  }

  return (
    <span className={styles.walletOptionIcon} aria-hidden="true">
      <Wallet size={18} />
    </span>
  );
}

function AssetDialog(props: {
  config: DisplayChainConfig | null;
  open: boolean;
  selectedTokenId: string;
  tokens: TokenOption[];
  onOpenChange: (open: boolean) => void;
  onSelect: (tokenId: string) => void;
}) {
  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Dialog.Content className={styles.dialogContent}>
          <div className={styles.dialogHeader}>
            <Dialog.Title>Select asset</Dialog.Title>
            <Dialog.Close className={styles.iconButton} aria-label="Close">
              <X size={16} aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div className={styles.assetList}>
            {props.tokens.map((token) => (
              <button
                key={token.id}
                className={styles.assetOption}
                type="button"
                onClick={() => props.onSelect(token.id)}
              >
                <TokenBadge config={props.config} token={token} />
                <span className={styles.assetOptionText}>
                  <strong>{token.symbol}</strong>
                  <span>{token.name}</span>
                </span>
                {token.id === props.selectedTokenId ? (
                  <CheckCircle2 size={18} aria-hidden="true" />
                ) : null}
              </button>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function GoogleLoginButton(props: {
  clientId: string;
  onCredential: (jwt: string) => Promise<void>;
  onError: (error: unknown) => void;
}) {
  const [pending, setPending] = useState(false);

  const connect = useCallback(() => {
    if (pending) {
      return;
    }
    setPending(true);
    void requestGoogleIdToken({ clientId: props.clientId })
      .then((jwt) => props.onCredential(jwt))
      .catch(props.onError)
      .finally(() => setPending(false));
  }, [pending, props.clientId, props.onCredential, props.onError]);

  return (
    <button
      className={`${styles.primaryButton} ${styles.googleFallbackButton}`}
      type="button"
      disabled={pending}
      onClick={connect}
    >
      {pending ? "Waiting for Google" : "Continue with Google"}
    </button>
  );
}

function AssetTotals(props: {
  claimTokenDecimals: Record<string, number | null>;
  config: DisplayChainConfig | null;
  totals: BalanceTotal[];
}) {
  if (props.totals.length === 0) {
    return (
      <div className={styles.noFundsRow}>
        <strong>No funds available</strong>
        <span>{networkSymbol(props.config)}</span>
      </div>
    );
  }

  return (
    <div className={styles.assetRows}>
      {props.totals.map((total) => {
        const tokenKey = total.asset.toLowerCase();
        const token = balanceTotalToOption(
          props.config,
          total,
          props.claimTokenDecimals[tokenKey] ?? undefined,
        );
        return (
          <div className={styles.assetRow} key={total.asset}>
            <TokenBadge config={props.config} token={token} />
            <div className={styles.assetRowText}>
              <strong>{formatClaimAmount(total.amount, token)}</strong>
              <span>{token.symbol}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TokenBadge(props: { config: DisplayChainConfig | null; token: TokenOption }) {
  const fallbackStyle: CSSProperties = {
    background: fallbackGradient(props.token.chainAddress),
  };

  return (
    <span className={styles.tokenBadge}>
      {props.token.logoURI === undefined ? (
        <span className={styles.tokenFallback} style={fallbackStyle}>
          {tokenInitials(props.token.symbol)}
        </span>
      ) : (
        <img className={styles.tokenLogo} src={props.token.logoURI} alt="" />
      )}
      <span className={styles.networkBadge}>{networkSymbol(props.config)}</span>
    </span>
  );
}

function TransactionLink(props: { config: DisplayChainConfig | null; txHash: string }) {
  return <span className={styles.txHash}>{shortHash(props.txHash)}</span>;
}

function firstChain(config: PublicConfig | null): DisplayChainConfig | null {
  return (config?.chains.find(isSupportedChainConfig) as DisplayChainConfig | undefined) ?? null;
}

function firstEvmChain(config: PublicConfig | null): EvmChainConfig | null {
  return config?.chains.find(isEvmChainConfig) ?? null;
}

function firstCardanoChain(config: PublicConfig | null): CardanoChainConfig | null {
  return config?.chains.find(isCardanoChainConfig) ?? null;
}

function evmChainForChainId(config: PublicConfig, chainId: number): EvmChainConfig | null {
  return (
    config.chains.find(
      (chain): chain is EvmChainConfig =>
        isEvmChainConfig(chain) && chain.chain.chainId === chainId,
    ) ?? null
  );
}

function cardanoChainForNetworkId(
  config: PublicConfig,
  networkId: number,
): CardanoChainConfig | null {
  const chains = config.chains.filter(isCardanoChainConfig);
  if (networkId === 1) {
    return chains.find((chain) => chain.chain.network.toLowerCase() === "mainnet") ?? null;
  }
  return chains.find((chain) => chain.chain.network.toLowerCase() !== "mainnet") ?? null;
}

function isSupportedChainConfig(config: ChainConfig): config is DisplayChainConfig {
  return isEvmChainConfig(config) || isCardanoChainConfig(config);
}

function isEvmChainConfig(config: ChainConfig): config is EvmChainConfig {
  return config.chain.type === "evm";
}

function isCardanoChainConfig(config: ChainConfig): config is CardanoChainConfig {
  return config.chain.type === "cardano";
}

function sameChainConfig(left: DisplayChainConfig, right: DisplayChainConfig): boolean {
  return chainConfigKey(left) === chainConfigKey(right);
}

function chainConfigKey(config: DisplayChainConfig): string {
  return config.chain.type === "evm"
    ? `evm:${config.chain.chainId}`
    : `cardano:${config.chain.network.toLowerCase()}`;
}

function walletAccountKey(config: DisplayChainConfig, account: string): string {
  return config.chain.type === "evm" ? normalizeAddress(account) : account.toLowerCase();
}

function isNativeAsset(config: DisplayChainConfig | null, asset: string): boolean {
  return asset.toLowerCase() === (config?.nativeAsset.toLowerCase() ?? ZERO_ADDRESS);
}

function balanceTotals(config: DisplayChainConfig | null, cells: BalanceResponse): BalanceTotal[] {
  const totals = new Map<string, BalanceTotal>();
  for (const cell of cells) {
    const key = cell.asset.toLowerCase();
    const current = totals.get(key);
    if (current === undefined) {
      totals.set(key, {
        asset: cell.asset,
        amount: cell.amount,
        display: cell.display,
      });
      continue;
    }
    current.amount = (BigInt(current.amount) + BigInt(cell.amount)).toString();
    current.display ??= cell.display;
  }

  return [...totals.values()].sort((left, right) => {
    const leftNative = isNativeAsset(config, left.asset);
    const rightNative = isNativeAsset(config, right.asset);
    if (leftNative !== rightNative) {
      return leftNative ? -1 : 1;
    }
    return (left.display?.symbol ?? left.asset).localeCompare(right.display?.symbol ?? right.asset);
  });
}

function defaultTokenOptions(config: DisplayChainConfig | null): TokenOption[] {
  if (config === null) {
    return [];
  }

  return [nativeTokenOption(config)];
}

function walletAssetToOption(config: DisplayChainConfig, asset: WalletAsset): TokenOption {
  const isNative = isNativeAsset(config, asset.asset);
  const symbol = asset.display?.symbol ?? shortAsset(asset.asset);
  const option: TokenOption = {
    id: isNative ? NATIVE_TOKEN_ID : asset.asset.toLowerCase(),
    address: isNative ? null : asset.asset,
    chainAddress: asset.asset,
    name: asset.display?.name ?? symbol,
    symbol,
    decimals: asset.display?.decimals ?? (config.chain.type === "cardano" ? 0 : null),
    native: isNative,
  };
  if (asset.display?.image !== undefined) {
    option.logoURI = asset.display.image;
  }
  return option;
}

function walletAssetBalances(tokens: TokenOption[], assets: WalletAsset[]): Record<string, string> {
  return Object.fromEntries(
    tokens.map((token, index): [string, string] => [token.id, assets[index]?.amount ?? "0"]),
  );
}

function nativeTokenOption(config: DisplayChainConfig): TokenOption {
  const symbol = nativeSymbol(config);
  const option: TokenOption = {
    id: NATIVE_TOKEN_ID,
    address: null,
    chainAddress: config.nativeAsset,
    name: `${config.name} ${symbol}`,
    symbol,
    decimals: nativeDecimals(config),
    native: true,
  };
  return option;
}

function nativeSymbol(config: DisplayChainConfig): string {
  if (config.chain.type === "cardano") {
    return "ADA";
  }
  return config.nativeAsset.toLowerCase() === ZERO_ADDRESS ? "ETH" : "Native";
}

function nativeDecimals(config: DisplayChainConfig): number {
  return config.chain.type === "cardano" ? 6 : 18;
}

function shortAsset(asset: string): string {
  return asset.length <= 12 ? asset : `${asset.slice(0, 6)}...${asset.slice(-4)}`;
}

function balanceTotalToOption(
  config: DisplayChainConfig | null,
  total: BalanceTotal,
  decimalsOverride?: number,
): TokenOption {
  const isNative = isNativeAsset(config, total.asset);
  const symbol = total.display?.symbol ?? shortAsset(total.asset);
  const option: TokenOption = {
    id: isNative ? NATIVE_TOKEN_ID : total.asset.toLowerCase(),
    address: isNative ? null : total.asset,
    chainAddress: total.asset,
    name: total.display?.name ?? symbol,
    symbol,
    decimals:
      total.display?.decimals ??
      decimalsOverride ??
      (isNative && config !== null ? nativeDecimals(config) : null),
    native: isNative,
  };
  if (total.display?.image !== undefined) {
    option.logoURI = total.display.image;
  }
  return option;
}

function sendSecondaryLine(
  config: DisplayChainConfig | null,
  wrongNetwork: boolean,
  relayFeeEstimate: string | null,
): string {
  if (config === null) {
    return "Loading network";
  }
  if (wrongNetwork) {
    return `Switch to ${config.name}`;
  }
  if (relayFeeEstimate === null) {
    return "Relay fee estimated before send";
  }
  return `Relay fee ${formatNative(config, relayFeeEstimate)}`;
}

function sendActionLabel(args: {
  amountExceedsBalance: boolean;
  busy: BusyState;
  balancesLoading: boolean;
  config: DisplayChainConfig | null;
  selectedToken: TokenOption | null;
  spendableTokenOptions: TokenOption[];
  tokenBalances: Record<string, string> | null;
  walletConnected: boolean;
  wrongNetwork: boolean;
  sendEmail: string;
  sendAmount: string;
}): string {
  if (args.busy === "send") {
    return "Sending";
  }
  if (args.config === null) {
    return "Loading network";
  }
  if (args.wrongNetwork) {
    return `Switch to ${args.config.name}`;
  }
  if (args.walletConnected && args.balancesLoading) {
    return "Loading assets";
  }
  if (
    args.walletConnected &&
    args.tokenBalances !== null &&
    args.spendableTokenOptions.length === 0
  ) {
    return "No assets available";
  }
  if (
    args.walletConnected &&
    args.tokenBalances !== null &&
    args.selectedToken !== null &&
    args.spendableTokenOptions.every((token) => token.id !== args.selectedToken?.id)
  ) {
    return "Select asset";
  }
  if (args.sendEmail.trim() === "") {
    return "Enter Gmail recipient";
  }
  if (args.selectedToken === null) {
    return "Select asset";
  }
  if (args.sendAmount.trim() === "") {
    return "Enter amount";
  }
  if (args.amountExceedsBalance) {
    return "Insufficient balance";
  }
  return "Send";
}

function sendAmountExceedsSelectedBalance(args: {
  config: DisplayChainConfig | null;
  relayFeeEstimate: string | null;
  selectedToken: TokenOption | null;
  sendAmount: string;
  tokenBalances: Record<string, string> | null;
}): boolean {
  if (
    args.selectedToken === null ||
    args.selectedToken.decimals === null ||
    args.tokenBalances === null ||
    args.sendAmount.trim() === ""
  ) {
    return false;
  }

  try {
    const amount = parseUnits(args.sendAmount, args.selectedToken.decimals);
    const available = BigInt(args.tokenBalances[args.selectedToken.id] ?? "0");
    if (args.selectedToken.native) {
      const relayFee = args.relayFeeEstimate === null ? 0n : BigInt(args.relayFeeEstimate);
      return amount + relayFee > available;
    }

    const nativeBalance = BigInt(args.tokenBalances[NATIVE_TOKEN_ID] ?? "0");
    const relayFee = args.relayFeeEstimate === null ? 0n : BigInt(args.relayFeeEstimate);
    const minimumNative =
      args.config?.chain.type === "cardano" ? CARDANO_MIN_CLAIM_PAYOUT_LOVELACE : 0n;
    return amount > available || relayFee + minimumNative > nativeBalance;
  } catch {
    return false;
  }
}

function claimSecondaryLine(
  config: DisplayChainConfig | null,
  wrongNetwork: boolean,
  claimableCells: number,
): string {
  if (config === null) {
    return "Loading network";
  }
  if (wrongNetwork) {
    return `Switch to ${config.name}`;
  }
  return claimableCells === 0
    ? "No funds available"
    : `${claimableCells} payment${claimableCells === 1 ? "" : "s"}`;
}

function claimButtonLabel(busy: BusyState, topUp: RelayTopUpState | null): string {
  if (busy === "claim") {
    return "Claiming";
  }
  if (busy === "topup") {
    return "Submitting";
  }
  return topUp === null ? "Claim" : "Top up relay fee";
}

function formatClaimAmount(amount: string, token: TokenOption): string {
  return token.decimals === null ? amount : formatUnits(BigInt(amount), token.decimals);
}

function formatNative(config: DisplayChainConfig | null, valueWei: string): string {
  const symbol = config === null ? "Native" : nativeSymbol(config);
  const decimals = config === null ? 18 : nativeDecimals(config);
  return `${formatUnits(BigInt(valueWei), decimals)} ${symbol}`;
}

function networkSymbol(config: DisplayChainConfig | null): string {
  return config === null ? "NET" : nativeSymbol(config).slice(0, 4).toUpperCase();
}

function tokenInitials(symbol: string): string {
  const compact = symbol
    .replace(/[^a-zA-Z0-9]/gu, "")
    .slice(0, 3)
    .toUpperCase();
  return compact === "" ? "?" : compact;
}

function fallbackGradient(seed: string): string {
  const defaultColors: readonly [string, string] = ["#4dbeff", "#2f7de1"];
  const palette: Array<readonly [string, string]> = [
    defaultColors,
    ["#56c596", "#178f68"],
    ["#ffb84d", "#e46d43"],
    ["#a78bfa", "#6d5bd0"],
    ["#f87171", "#d9467f"],
  ];
  let hash = 0;
  for (const char of seed.toLowerCase()) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  const colors = palette[hash % palette.length] ?? defaultColors;
  return `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`;
}

function shortHash(hash: string): string {
  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
}

function shortWalletAddress(account: string, config: DisplayChainConfig | null): string {
  if (config?.chain.type === "evm") {
    return shortAddress(account);
  }
  return shortHash(account);
}

function errorMessage(error: unknown): string {
  const extracted = extractErrorMessage(error, new Set());
  return extracted ?? String(error);
}

function extractErrorMessage(error: unknown, seen: Set<unknown>): string | null {
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message;
  }
  if (error === null || typeof error !== "object" || seen.has(error)) {
    return null;
  }
  seen.add(error);

  const record = error as Record<string, unknown>;
  for (const key of ["message", "shortMessage", "reason", "details"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
  }

  for (const key of ["data", "error", "cause"]) {
    const nested = extractErrorMessage(record[key], seen);
    if (nested !== null) {
      return nested;
    }
  }

  try {
    return JSON.stringify(error);
  } catch {
    return null;
  }
}

function parseRelayFeeTopUp(error: unknown): ParsedRelayTopUp | null {
  if (error instanceof ApiRequestError && error.relayFeeTopUp !== undefined) {
    const requiredWei = BigInt(error.relayFeeTopUp.requiredWei);
    const availableWei = BigInt(error.relayFeeTopUp.availableWei);
    const shortfallWei = BigInt(error.relayFeeTopUp.shortfallWei);
    if (shortfallWei === 0n || error.relayFeeTopUp.suggestedTopUpCellId === "") {
      return null;
    }

    return {
      requiredWei,
      availableWei,
      shortfallWei,
      suggestedTopUpCellId: error.relayFeeTopUp.suggestedTopUpCellId,
    };
  }

  return null;
}

function parsePendingClaim(error: unknown): PendingClaimError | null {
  if (error instanceof ApiRequestError && error.pendingClaim !== undefined) {
    return error.pendingClaim;
  }

  return null;
}

function showPendingClaimToasts(
  showToast: (kind: ToastKind, title: string, options?: ToastOptions) => void,
  pendingClaim: PendingClaimError,
) {
  if (pendingClaim.txHashes.length === 0) {
    showToast("info", "Claim already pending", {
      body: `${pendingClaim.cellIds.length} payment${pendingClaim.cellIds.length === 1 ? "" : "s"}`,
    });
    return;
  }

  for (const txHash of pendingClaim.txHashes) {
    showToast("info", "Claim already pending", { txHash });
  }
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunkSize = Math.max(1, Math.floor(size));
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}
